import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { TrendingDown, TrendingUp, Minus } from "lucide-react";
import type { PriceLabel } from "@/hooks/usePriceBenchmark";

interface Props {
  label: PriceLabel;
  diffPct: number | null;
  sampleSize?: number;
  size?: "sm" | "md";
}

const CONFIG: Record<
  PriceLabel,
  { text: string; className: string; Icon: React.ComponentType<{ className?: string }> }
> = {
  good: {
    text: "Preț bun",
    className: "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800",
    Icon: TrendingDown,
  },
  market: {
    text: "Preț de piață",
    className: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800",
    Icon: Minus,
  },
  above: {
    text: "Peste medie",
    className: "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800",
    Icon: TrendingUp,
  },
};

export const PriceBenchmarkBadge = ({ label, diffPct, sampleSize, size = "sm" }: Props) => {
  const { text, className, Icon } = CONFIG[label];
  const pct = diffPct != null ? Math.round(Math.abs(diffPct)) : null;
  const detail =
    diffPct == null || pct === 0
      ? text
      : diffPct > 0
        ? `${text} · cu ${pct}% sub medie`
        : `${text} · cu ${pct}% peste medie`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className={`gap-1 font-medium ${className} ${size === "sm" ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2 py-1"}`}
        >
          <Icon className={size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5"} />
          {text}
        </Badge>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="center"
        collisionPadding={12}
        avoidCollisions
        className="z-50 w-[min(20rem,calc(100vw-2rem))] max-w-[calc(100vw-2rem)] whitespace-normal break-words"
      >
        <p className="text-xs break-words">{detail}</p>
        {sampleSize && (
          <p className="text-[10px] text-muted-foreground mt-1 break-words">
            Comparație cu {sampleSize} mașini similare (marcă, model, an, kilometraj).
          </p>
        )}
      </TooltipContent>
    </Tooltip>
  );
};
